/**
 * Advisor quick navigation rendered as floating pills.
 */
import { useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { Box, IconButton, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import HomeIconSrc from "../../../assets/images/HomePage/Icons/home.svg";
import InfoIconSrc from "../../../assets/images/HomePage/Icons/info-empty.svg";
import QrIconSrc from "../../../assets/images/HomePage/Icons/scan-qr-code.svg";
import ArticleIconSrc from "../../../assets/images/HomePage/Icons/page-flip.svg";
import CostsIconSrc from "../../../assets/images/HomePage/Icons/money-square.svg";
import LogsIconSrc from "../../../assets/images/HomePage/Icons/task-list.svg";
import DashboardIconSrc from "../../../assets/images/HomePage/Icons/view-structure-up.svg";
import { primaytheme } from "../../../assets/styles/generaltheme";
import { AdministrationPanelProvider } from "../../../pages/AdministrationPanel/AdministrationContext/AdministrationPanelContext";
import { useAuth } from "../../../context/AuthContext/AuthContext";
import Article51 from "./Article51/Article51";
import Costs from "./Costs/Costs";
import Dashboard from "./Dashboard/Dashboard";
import InfographyContent from "./InfographyContent/InfographyContent";
import QRContent from "./QRContent/QRContent";
import { TransactionLogs } from "./TransactionLogs/TransactionLogs";

const navItems = [
  { value: 0, label: "Inicio", iconSrc: HomeIconSrc },
  { value: 1, label: "Infografía", iconSrc: InfoIconSrc },
  { value: 2, label: "QR", iconSrc: QrIconSrc },
  { value: 3, label: "Article 51", iconSrc: ArticleIconSrc },
  { value: 4, label: "Costs", iconSrc: CostsIconSrc },
  { value: 5, label: "Dashboard", iconSrc: DashboardIconSrc },
  { value: 6, label: "Transaction Logs", iconSrc: LogsIconSrc },
];

type IconNavigationProps = {
  forceVisible?: boolean;
};

const IconNavigation = ({ forceVisible = false }: IconNavigationProps) => {
  const { getRoutePermissions } = useAuth();
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const isPaymentMode = forceVisible;
  const isOverlayOpen = value !== 0;

  const renderContent = () => {
    switch (value) {
      case 1:
        return <InfographyContent paymentMode={isPaymentMode} />;
      case 2:
        return <QRContent paymentMode={isPaymentMode} />;
      case 3:
        return <Article51 paymentMode={isPaymentMode} />;
      case 4:
        return <Costs paymentMode={isPaymentMode} />;
      case 5:
        return <Dashboard paymentMode={isPaymentMode} />;
      case 6:
        return <TransactionLogs setValue={setValue} paymentMode={isPaymentMode} />;
      default:
        return null;
    }
  };

  const renderItemIcon = (isActive: boolean, iconSrc: string, label: string) => {
    const activeFilter = "brightness(0) invert(1)";
    const inactiveFilter =
      "brightness(0) saturate(100%) invert(13%) sepia(0%) saturate(0%) hue-rotate(136deg) brightness(96%) contrast(91%)";

    return (
      <Box
        component="img"
        src={iconSrc}
        alt={label}
        sx={{
          width: 20,
          height: 20,
          display: "block",
          filter: isActive ? activeFilter : inactiveFilter,
        }}
      />
    );
  };

  if (!forceVisible && !getRoutePermissions("/PrincipalPage").navigationbar) return null;

  return (
    <ThemeProvider theme={primaytheme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          ...(isPaymentMode
            ? {
                position: { xs: "static", md: isOverlayOpen ? "fixed" : "static" },
                top: { md: 0 },
                right: { md: 0 },
                width: { xs: "100%", md: isOverlayOpen ? "60%" : "100%" },
                height: { md: isOverlayOpen ? "100vh" : "auto" },
                zIndex: isOverlayOpen ? 1200 : "auto",
              }
            : {}),
        }}
      >
        <Box
          sx={{
            display: isOverlayOpen ? "block" : "none",
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            p: 0,
            pb: isPaymentMode ? { xs: "84px", md: "92px" } : 0,
            height: isPaymentMode ? { md: "100%" } : "auto",
          }}
        >
          <AdministrationPanelProvider>{renderContent()}</AdministrationPanelProvider>
        </Box>

        <Box
          sx={{
            backgroundColor: isPaymentMode ? "transparent" : "#fafafa",
            bottom: 0,
            borderTop: isPaymentMode ? "none" : "1px solid #E3E3E3",
            display: "flex",
            justifyContent: "center",
            position: "fixed",
            ...(isPaymentMode
              ? {
                  right: { md: 0 },
                  left: { xs: 0, md: "auto" },
                  transform: "none",
                  width: { xs: "100%", md: "60%" },
                }
              : {
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "100%",
                }),
            zIndex: 1400,
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              backgroundColor: isPaymentMode ? { xs: "#fafafa", md: "transparent" } : "transparent",
              borderTop: "none",
              borderRadius: 0,
              boxShadow: "none",
              display: "flex",
              gap: { xs: "8px", md: "20px" },
              justifyContent: "center",
              maxWidth: "100%",
              pb: { xs: "calc(8px + env(safe-area-inset-bottom))", md: "8px" },
              pt: { xs: "8px", md: "8px" },
              px: { xs: "12px", md: 0 },
              width: isPaymentMode ? { xs: "auto", md: "100%" } : "100%",
            }}
          >
            {navItems.map((item) => {
              const isActive = value === item.value;

              if (isActive) {
                return (
                  <Box
                    key={item.value}
                    onClick={() => {
                      setValue(item.value);
                      if (item.value === 0 && !isPaymentMode) {
                        navigate("/");
                      }
                    }}
                    sx={{
                      alignItems: "center",
                      bgcolor: "#761542",
                      borderRadius: "8px",
                      color: "#F9F9F9",
                      cursor: "pointer",
                      display: "inline-flex",
                      gap: { xs: 0, md: "8px" },
                      height: "37px",
                      justifyContent: "center",
                      px: { xs: 0, md: "8px" },
                      width: { xs: "37px", md: "auto" },
                    }}
                  >
                    <Typography
                      sx={{
                        display: { xs: "none", md: "block" },
                        fontFamily: "Candara",
                        fontSize: "16px",
                        fontWeight: 700,
                        lineHeight: "20px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.label}
                    </Typography>
                    {renderItemIcon(isActive, item.iconSrc, item.label)}
                  </Box>
                );
              }

              return (
                <IconButton
                  key={item.value}
                  onClick={() => {
                    setValue(item.value);
                    if (item.value === 0 && !isPaymentMode) {
                      navigate("/");
                    }
                  }}
                  sx={{
                    borderRadius: "8px",
                    color: isActive ? "#F9F9F9" : "#232323",
                    height: "37px",
                    p: "8px",
                    width: "37px",
                    bgcolor: isActive ? "#761542" : "transparent",
                    "&:hover": {
                      bgcolor: isActive ? "#761542" : "rgba(118, 21, 66, 0.08)",
                    },
                  }}
                >
                  {renderItemIcon(isActive, item.iconSrc, item.label)}
                </IconButton>
              );
            })}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default IconNavigation;
